import { useEffect, useRef, useState, useCallback } from "react";

/**
 * useAutosave — debounced autosave hook for builder forms.
 *
 * @param {any} data - The form data object to autosave. Watched via JSON equality.
 * @param {Function} saveFn - async (data) => any. Called when a save fires.
 * @param {Object} opts
 * @param {number} [opts.delay=1500] - Debounce in ms.
 * @param {boolean} [opts.enabled=true] - Disable autosave entirely (e.g. while loading).
 * @param {Function} [opts.shouldSave] - (data) => boolean. Skip empty/invalid states.
 *
 * @returns {{ status, lastSavedAt, flush }}
 *   status: 'idle' | 'pending' | 'saving' | 'saved' | 'error'
 *   lastSavedAt: Date | null
 *   flush: () => Promise<void>  // save NOW (await for navigation/unmount)
 */
export default function useAutosave(data, saveFn, opts = {}) {
  const { delay = 1500, enabled = true, shouldSave } = opts;

  const [status, setStatus] = useState("idle");
  const [lastSavedAt, setLastSavedAt] = useState(null);

  // Stable refs for latest data + saveFn so the debounced timer
  // never operates on stale closures.
  const dataRef = useRef(data);
  const saveFnRef = useRef(saveFn);
  const shouldSaveRef = useRef(shouldSave);
  const lastSavedJsonRef = useRef(null);
  const timerRef = useRef(null);
  const inFlightRef = useRef(false);
  const skipNextRef = useRef(true); // skip the initial mount

  useEffect(() => { dataRef.current = data; }, [data]);
  useEffect(() => { saveFnRef.current = saveFn; }, [saveFn]);
  useEffect(() => { shouldSaveRef.current = shouldSave; }, [shouldSave]);

  const doSave = useCallback(async () => {
    if (inFlightRef.current) return;
    const snapshot = dataRef.current;
    const json = safeJson(snapshot);
    if (json === lastSavedJsonRef.current) return;
    if (shouldSaveRef.current && !shouldSaveRef.current(snapshot)) return;

    inFlightRef.current = true;
    setStatus("saving");
    try {
      await saveFnRef.current(snapshot);
      lastSavedJsonRef.current = json;
      setLastSavedAt(new Date());
      setStatus("saved");
    } catch (err) {
      // Keep the form usable — autosave failures should not block UI.
      console.warn("autosave failed", err?.message || err);
      setStatus("error");
    } finally {
      inFlightRef.current = false;
    }
  }, []);

  // Watch data changes → schedule debounced save.
  useEffect(() => {
    if (!enabled) return;
    if (skipNextRef.current) {
      skipNextRef.current = false;
      lastSavedJsonRef.current = safeJson(data);
      return;
    }
    const json = safeJson(data);
    if (json === lastSavedJsonRef.current) return;

    setStatus("pending");
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      doSave();
    }, delay);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [data, enabled, delay]);

  // Flush pending save synchronously-ish — used on step nav / unmount.
  const flush = useCallback(async () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    await doSave();
  }, [doSave]);

  // Save on unmount if a pending change is in the buffer.
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        // Fire-and-forget; component is unmounting so no state updates matter.
        doSave();
      }
    };
  }, []);

  // beforeunload — warn if the user closes the tab mid-typing.
  useEffect(() => {
    const onBeforeUnload = (e) => {
      if (status === "pending" || status === "saving") {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [status]);

  return { status, lastSavedAt, flush };
}

function safeJson(v) {
  try {
    return JSON.stringify(v);
  } catch (_) {
    return null;
  }
}
